"use client";

/**
 * The small promises a product makes on the website (Petal_Some Ug., Oct 2026):
 *   • "Free delivery" — products.freeDeliveryOnline. A delivery is free only
 *     when every item in the bag has it (lib/config/websiteAddOns.ts).
 *   • what it comes with / the extras it offers — products.hireKit.
 * Cards show the chips (WebsitePerkTags); the product page shows the badge
 * and, under the button, the rule (FreeDeliveryNote).
 */

import { Gift, Plus, Truck } from "lucide-react";
import { FREE_DELIVERY_RULE, isPaidAddOn, websiteAddOns } from "@/lib/config/websiteAddOns";
import { isHireOnly } from "@/lib/config/hire";

export function FreeDeliveryBadge({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 whitespace-nowrap ${className}`}
      title={`Free delivery on website orders. ${FREE_DELIVERY_RULE}`}
    >
      <Truck className="h-3 w-3" aria-hidden />
      Free delivery
    </span>
  );
}

/**
 * A card's chips: "Free delivery", and "+ add-ons" when the shopper can add
 * extras — or "Comes with …" when the pieces are all free. Nothing for a
 * hire-only item (its hire panel lists the kit).
 */
export function WebsitePerkTags({ product, className = "" }: { product: any; className?: string }) {
  const pieces = isHireOnly(product) ? [] : websiteAddOns(product?.hireKit);
  const freeDelivery = product?.freeDeliveryOnline === true;
  if (!freeDelivery && pieces.length === 0) return null;
  const paid = pieces.some(isPaidAddOn);
  const free = pieces.filter((p) => !isPaidAddOn(p));
  const comesWith = free.map((p) => `${p.qty} × ${p.name}`).join(", ");
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`}>
      {freeDelivery && <FreeDeliveryBadge />}
      {paid ? (
        <span
          className="inline-flex items-center gap-0.5 rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700 whitespace-nowrap"
          title={comesWith ? `Comes with ${comesWith}. Extras on the product page.` : "Extras on the product page"}
        >
          <Plus className="h-2.5 w-2.5" aria-hidden />
          add-ons
        </span>
      ) : free.length > 0 ? (
        <span
          className="inline-flex max-w-full items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700"
          title={`Comes with ${comesWith}`}
        >
          <Gift className="h-2.5 w-2.5 shrink-0" aria-hidden />
          <span className="truncate">
            Comes with {free[0].name}
            {free.length > 1 ? ` +${free.length - 1}` : ""}
          </span>
        </span>
      ) : null}
    </div>
  );
}

/** The product page's line under the button: free delivery, and the rule. */
export function FreeDeliveryNote({ className = "" }: { className?: string }) {
  return (
    <div className={`flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-900 ${className}`}>
      <Truck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
      <p>
        <span className="font-semibold">Free delivery</span> on this item when you order on this website.{" "}
        {FREE_DELIVERY_RULE}
      </p>
    </div>
  );
}
